import { useSyncExternalStore } from 'react'

const WATCHED_UNITS_STORAGE_KEY = 'matevolno.watchedUnits.v1'

/** Unit ID → the e-mail its watchman was subscribed with (needed to unsubscribe). */
type WatchedUnits = Record<number, string>

const EMPTY: WatchedUnits = {}

const listeners = new Set<() => void>()
let cache: WatchedUnits | null = null

function read(): WatchedUnits {
  if (cache) return cache
  try {
    const raw = window.localStorage.getItem(WATCHED_UNITS_STORAGE_KEY)
    const parsed = raw ? (JSON.parse(raw) as unknown) : null
    cache = parsed && typeof parsed === 'object' ? (parsed as WatchedUnits) : EMPTY
  } catch {
    cache = EMPTY
  }
  return cache
}

function write(next: WatchedUnits): void {
  cache = next
  try {
    window.localStorage.setItem(WATCHED_UNITS_STORAGE_KEY, JSON.stringify(next))
  } catch {
    // Ignore private mode / quota / disabled storage; the heart still works
    // for this page view.
  }
  listeners.forEach((listener) => listener())
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

export function markUnitWatched(unitId: number, email: string): void {
  write({ ...read(), [unitId]: email })
}

export function unmarkUnitWatched(unitId: number): void {
  const { [unitId]: _removed, ...rest } = read()
  write(rest)
}

/** The e-mail the unit is watched with, or undefined when it isn't watched. */
export function useUnitWatchEmail(unitId: number): string | undefined {
  return useSyncExternalStore(
    subscribe,
    () => read()[unitId],
    () => undefined,
  )
}
